import Link from "next/link";
import { usePathname } from "next/navigation";
import React, { Fragment } from "react";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import classNames from "@calcom/ui/classNames";
import { Icon } from "@calcom/ui/components/icon";
import type { IconName } from "@calcom/ui/components/icon";
import { SkeletonText } from "@calcom/ui/components/skeleton";
import { Tooltip } from "@calcom/ui/components/tooltip";

import { useShouldDisplayNavigationItem } from "./useShouldDisplayNavigationItem";
import {UserPermissionRole} from "../../../prisma/enums";
import {useSession} from "next-auth/react";

export type NavigationItemType = {
  name: string;
  href: string;
  isLoading?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  role?: string;
  userType?: string; // "all" | "MARKETPLACE" | "PLATFORM" | "ORGANIZATION"
  userCategory?: string; // "all" | "PRACTITIONER" | "PRACTICE" | "PHARMACY"
  target?: HTMLAnchorElement["target"];
  badge?: React.ReactNode;
  icon?: IconName;
  customIconElement?: React.ReactNode;
  customIcon?: boolean;
  child?: NavigationItemType[];
  pro?: true;
  onlyMobile?: boolean;
  onlyDesktop?: boolean;
  moreOnMobile?: boolean;
  isCurrent?: ({
    item,
    isChild,
    pathname,
  }: {
    item: Pick<NavigationItemType, "href">;
    isChild?: boolean;
    pathname: string | null;
  }) => boolean;
};

const defaultIsCurrent: NavigationItemType["isCurrent"] = ({ isChild, item, pathname }) => {
  return isChild ? item.href === pathname : item.href ? pathname?.startsWith(item.href) ?? false : false;
};

export const NavigationItem: React.FC<{
  index?: number;
  item: NavigationItemType;
  showAdminOnly?:boolean;
  isChild?: boolean;
  isCollapsed?: boolean;
}> = (props) => {
  const session = useSession();
  const isAdmin = session.data?.user.role?.includes('ADMIN');
  const userCategory = session.data?.user.userCategory;
  const { item, isChild, isCollapsed = false, showAdminOnly } = props;
  const { t, isLocaleReady } = useLocale();
  const pathname = usePathname();
  const isCurrent: NavigationItemType["isCurrent"] = item.isCurrent || defaultIsCurrent;
  const current = isCurrent({ isChild: !!isChild, item, pathname });
  const shouldDisplayNavigationItem = useShouldDisplayNavigationItem(props.item);

  // Check if item should be visible based on role, userType, and userCategory
  // If showAdminOnly is true, use OR logic: show if user is admin OR (userType matches AND item has specific userType) OR (userCategory matches AND item has specific userCategory)
  let shouldShowItem = true;
  if (showAdminOnly) {
    const userType = session.data?.user.userType;
    const itemUserType = item.userType || "all";
    const itemUserCategory = item.userCategory || "all";
    
    // Only consider userType/userCategory if they are NOT "all" (i.e., item has specific restrictions)
    const hasUserTypeRestriction = itemUserType !== "all";
    const hasUserCategoryRestriction = itemUserCategory !== "all";
    
    const userTypeMatches = hasUserTypeRestriction && itemUserType === userType;
    const userCategoryMatches = hasUserCategoryRestriction && itemUserCategory === userCategory;
    
    // Show if user is admin OR if userType matches (when item has userType restriction) OR if userCategory matches (when item has userCategory restriction)
    shouldShowItem = isAdmin || userTypeMatches || userCategoryMatches;
  }

  if (!shouldDisplayNavigationItem || !shouldShowItem) return null;

  return (
    <Fragment>
      <Tooltip side="right" content={t(item.name)} className="lg:hidden">
        {item.onClick ? (
          <button
            data-test-id={item.name}
            onClick={item.onClick}
            aria-label={t(item.name)}
            className={classNames(
              "todesktop:py-[7px] text-default group flex w-full items-center rounded-xl px-2 py-1.5 text-sm font-medium transition",
              item.child
                ? `[&[aria-current='page']]:!bg-transparent`
                : `[&[aria-current='page']]:!bg-[var(--primary-main)]`,
              isChild
                ? `[&[aria-current='page']]:!bg-[var(--primary-main)] hidden h-8 pl-16 lg:flex lg:pl-11 [&[aria-current='page']]:!text-white ${
                    props.index === 0 ? "mt-0" : "mt-px"
                  }`
                : "mt-0.5 text-sm [&[aria-current='page']]:!text-white",
              isLocaleReady
                ? "hover:!bg-[var(--primary-main)] hover:opacity-80 todesktop:[&[aria-current='page']]:!bg-[var(--primary-main)] todesktop:hover:bg-transparent hover:!text-white"
                : ""
            )}
            aria-current={current ? "page" : undefined}>
            {item.icon && !item.customIcon && (
              <Icon
                name={item.isLoading ? "rotate-cw" : item.icon}
                className={classNames(
                  "todesktop:!text-blue-500 h-4 w-4 flex-shrink-0 [&[aria-current='page']]:text-inherit",
                  isCollapsed ? "mx-auto" : "mr-2 rtl:ml-2 md:ltr:mx-auto lg:ltr:mr-2",
                  item.isLoading && "animate-spin"
                )}
                aria-hidden="true"
                aria-current={current ? "page" : undefined}
              />
            )}
            {item.customIcon && (
              <div className={classNames(isCollapsed ? "mx-auto" : "mr-2 rtl:ml-2 md:ltr:mx-auto lg:ltr:mr-2")}>
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_385_15248)">
                    <path
                      d="M6 15.3353V8.66862H10V15.3353M2 6.66862L8 2.00195L14 6.66862V14.002C14 14.3556 13.8595 14.6947 13.6095 14.9448C13.3594 15.1948 13.0203 15.3353 12.6667 15.3353H3.33333C2.97971 15.3353 2.64057 15.1948 2.39052 14.9448C2.14048 14.6947 2 14.3556 2 14.002V6.66862Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_385_15248">
                      <rect width="16" height="16" fill="white" transform="translate(0 0.668701)" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            )}
            {isLocaleReady ? (
              <span
                className={classNames(
                  "hidden w-full justify-between truncate text-ellipsis lg:flex",
                  isCollapsed && "lg:hidden"
                )}
                data-testid={`${item.name}-test`}>
                {t(item.name)}
                {item.badge && item.badge}
              </span>
            ) : (
              <SkeletonText className="h-[20px] w-full" />
            )}
          </button>
        ) : (
          <Link
            data-test-id={item.name}
            href={item.href}
            aria-label={t(item.name)}
            target={item.target}
            className={classNames(
              "todesktop:py-[7px] text-default group flex items-center rounded-xl px-2 py-1.5 text-sm font-medium transition",
              item.child
                ? `[&[aria-current='page']]:!bg-transparent`
                : `[&[aria-current='page']]:!bg-[var(--primary-main)]`,
              isChild
                ? `[&[aria-current='page']]:!bg-[var(--primary-main)] hidden h-8 pl-16 lg:flex lg:pl-11 [&[aria-current='page']]:!text-white ${
                    props.index === 0 ? "mt-0" : "mt-px"
                  }`
                : item.child  ? "[&[aria-current='page']]:!text-default" : "[&[aria-current='page']]:!text-white",
              isLocaleReady
                ? (item.child ? "hover:opacity-80 todesktop:[&[aria-current='page']]:!bg-[var(--primary-main)] " : "hover:!bg-[var(--primary-main)] hover:opacity-80 todesktop:[&[aria-current='page']]:!bg-[var(--primary-main)] todesktop:hover:bg-transparent hover:!text-white")
                : ""
            )}
            aria-current={current ? "page" : undefined}>
            {item.customIconElement ? (
              <div className={classNames(isCollapsed ? "mx-auto" : "mr-2 rtl:ml-2 md:ltr:mx-auto lg:ltr:mr-2")}>
                {item.customIconElement}
              </div>
            ) : item.icon && (
              <Icon
                name={item.isLoading ? "rotate-cw" : item.icon}
                className={classNames(
                  "todesktop:!text-blue-500 h-4 w-4 flex-shrink-0 [&[aria-current='page']]:text-inherit",
                  isCollapsed ? "mx-auto" : "mr-2 rtl:ml-2 md:ltr:mx-auto lg:ltr:mr-2",
                  item.isLoading && "animate-spin"
                )}
                aria-hidden="true"
                aria-current={current ? "page" : undefined}
              />
            )}
            {isLocaleReady ? (
              <span
                className={classNames(
                  "hidden w-full justify-between truncate text-ellipsis lg:flex",
                  isCollapsed && "lg:hidden"
                )}
                data-testid={`${item.name}-test`}>
                {t(item.name)}
                {item.badge && item.badge}
              </span>
            ) : (
              <SkeletonText className="h-[20px] w-full" />
            )}
          </Link>
        )}
      </Tooltip>
      {item.child &&
        isCurrent({ pathname, isChild, item }) &&
        item.child.map((item, index) => <NavigationItem index={index} key={item.name} item={item} showAdminOnly={item.role === 'admin'} isChild isCollapsed={isCollapsed} />)}
    </Fragment>
  );
};

export const MobileNavigationItem: React.FC<{
  item: NavigationItemType;
  isChild?: boolean;
}> = (props) => {
  const { item, isChild } = props;
  const pathname = usePathname();
  const { t, isLocaleReady } = useLocale();
  const isCurrent: NavigationItemType["isCurrent"] = item.isCurrent || defaultIsCurrent;
  const current = isCurrent({ isChild: !!isChild, item, pathname });
  const shouldDisplayNavigationItem = useShouldDisplayNavigationItem(props.item);

  if (!shouldDisplayNavigationItem) return null;
  return (
    <Link
      key={item.name}
      href={item.href}
      target={item.target}
      className="[&[aria-current='page']]:!text-[var(--primary-main)] [&[aria-current='page']]:!bg-[var(--primary-tints-t1)] hover:text-default text-muted relative my-2 min-w-0 flex-1 overflow-hidden rounded-xl !bg-transparent p-1 text-center text-xs font-medium focus:z-10 sm:text-sm"
      aria-current={current ? "page" : undefined}>
      {item.badge && <div className="absolute right-1 top-1">{item.badge}</div>}
      {item.icon && (
        <Icon
          name={item.icon}
          className="[&[aria-current='page']]:!text-[var(--primary-main)] mx-auto mb-1 block h-5 w-5 flex-shrink-0 text-center text-inherit"
          aria-hidden="true"
          aria-current={current ? "page" : undefined}
        />
      )}
      {isLocaleReady ? <span className="block truncate">{t(item.name)}</span> : <SkeletonText />}
    </Link>
  );
};

export const MobileNavigationMoreItem: React.FC<{
  item: NavigationItemType;
  isChild?: boolean;
}> = (props) => {
  const { item } = props;
  const { t, isLocaleReady } = useLocale();
  const shouldDisplayNavigationItem = useShouldDisplayNavigationItem(props.item);

  if (!shouldDisplayNavigationItem) return null;

  return (
    <li className="border-subtle border-b last:border-b-0" key={item.name}>
      <Link href={item.href} className="hover:!bg-[var(--primary-tints-t1)] flex items-center justify-between p-5 transition">
        <span className="text-default flex items-center font-semibold ">
          {item.icon && (
            <Icon name={item.icon} className="h-5 w-5 flex-shrink-0 ltr:mr-3 rtl:ml-3" aria-hidden="true" />
          )}
          {isLocaleReady ? t(item.name) : <SkeletonText />}
        </span>
        <Icon name="arrow-right" className="text-subtle h-5 w-5" />
      </Link>
    </li>
  );
};
